import Icon from '@components/v2/ui/Icon/Icon';
import { TextFieldProps } from '@mui/material';
import {
  DatePickerSlotProps,
  DateValidationError,
  DatePicker as MuiDatePicker,
  PickerChangeHandlerContext,
  PickersActionBarAction,
} from '@mui/x-date-pickers';
import { Calendar } from 'lucide-react';
import React, { forwardRef, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  root: {
    '& > .MuiOutlinedInput-adornedEnd': {
      paddingRight: 0,
    },
  },
  dialog: {
    '& .MuiDialogActions-spacing > :first-of-type': {
      color: '#97999B !important',
    },
  },
}));

const DefaultOpenPickerIcon = () => <Icon icon={Calendar} size="lg" />;

export type InternalDatePickerProps = {
  name?: string;
  maxDate?: Date;
  label?: string;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  value?: Date | null;
  helperText?: string;
  placeholder?: string;
  disablePast?: boolean;
  invalidDateMessage?: string;
  InputProps?: TextFieldProps;
  size?: TextFieldProps['size'];
  keyboardIcon?: React.ElementType;
  onBlur?: TextFieldProps['onBlur'];
  onChange?: (date: Date | null, context: PickerChangeHandlerContext<DateValidationError>) => void;
  onClear?: () => void;
  onAccept?: (date: Date | null) => void;
  inputVariant?: 'standard' | 'outlined' | 'filled';
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  openPickerButtonProps?: DatePickerSlotProps<Date, false>['openPickerButton'];
};

/**
 * @deprecated não necessariamente este depraciado, mas sinto que este componente ficou um pouco sujo com a subida do mui-v5, creio que podemos refatorar ou criar outro para seguir usando
 */
const DatePicker = forwardRef<HTMLInputElement, InternalDatePickerProps>(
  (
    {
      placeholder = '',
      name,
      size,
      value,
      error,
      label,
      onBlur,
      maxDate,
      onChange,
      onClear,
      onAccept,
      fullWidth,
      disabled,
      required,
      clearable,
      className,
      helperText,
      disablePast,
      InputProps,
      inputVariant,
      keyboardIcon,
      InputLabelProps,
      invalidDateMessage,
      openPickerButtonProps,
    },
    ref,
  ) => {
    const { classes } = useStyles();

    const actions = useMemo<PickersActionBarAction[]>(() => {
      const actions: PickersActionBarAction[] = ['accept'];

      if (clearable) {
        actions.push('clear');
      }

      return actions;
    }, [clearable]);

    return (
      <MuiDatePicker
        name={name}
        value={value}
        label={label}
        inputRef={ref}
        maxDate={maxDate}
        onAccept={onAccept}
        disabled={disabled}
        className={className}
        disablePast={disablePast}
        onChange={(date, context) => {
          onChange?.(date, context);
        }}
        slots={{
          openPickerIcon: keyboardIcon || DefaultOpenPickerIcon,
        }}
        slotProps={{
          openPickerButton: {
            edge: false,
            ...openPickerButtonProps,
          },
          dialog: {
            className: classes.dialog,
          },
          actionBar: {
            actions,
          },
          field: {
            clearable: clearable,
            onClear: () => {
              if (onClear) {
                onClear();
                return;
              }
              onChange?.(null, { validationError: null });
            },
          },
          textField: {
            size,
            error,
            onBlur,
            required,
            fullWidth,
            placeholder,
            InputLabelProps,
            variant: inputVariant,
            helperText: error && invalidDateMessage ? invalidDateMessage : helperText,
            ...InputProps,
          },
        }}
      />
    );
  },
);

export default DatePicker;
